import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IndianRupee, ShieldCheck, Lock, HandCoins } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ensureCashfree } from "@/components/locanest/BookingButton";

export const PURPOSES = [["token", "Token / booking amount"], ["deposit", "Security deposit"], ["rent", "First month rent"], ["other", "Brokerage / other agreed amount"]];
export const PURPOSE_LABEL = Object.fromEntries(PURPOSES);

// Issue H — "Payment to Broker": tenant pays the owner/broker THROUGH LocaNest via Cashfree (money lands in
// LocaNest's account). Amount = owner's listed token/brokerage amount (pre-filled) or the amount agreed in chat.
// LocaNest holds it, notifies the broker, and admin transfers manually within 3–4 days (Admin → Payouts).
export default function ProtectedPayButton({ property, relatedBookingId = null, variant = "outline", className = "" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [purpose, setPurpose] = useState("token");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/config").then(({ data }) => setCfg(data)).catch(() => setCfg({})); }, []);
  useEffect(() => {
    if (property?.expected_payment_inr) setAmount(String(property.expected_payment_inr));
    if (property?.expected_payment_purpose) setPurpose(property.expected_payment_purpose);
  }, [property?.expected_payment_inr, property?.expected_payment_purpose]);

  if (!cfg) return null;
  const enabled = !!cfg.protected_payment_enabled;
  const minInr = cfg.protected_payment_min_inr || 1;
  const amt = Number(amount || 0);
  const ownerSet = Number(property?.expected_payment_inr || 0);

  const pay = async () => {
    if (!user) return navigate(`/login?next=${encodeURIComponent(`/property/${property.id}`)}`);
    if (user.role !== "tenant") return toast.error("Only tenant accounts can pay through LocaNest.");
    if (!Number.isInteger(amt) || amt < minInr) return toast.error(`Enter a whole amount of at least ₹${minInr}`);
    setBusy(true);
    try {
      const { data } = await api.post("/bookings/protected-payment", { property_id: property.id, amount_inr: amt, purpose, note: note || null, related_booking_id: relatedBookingId });
      setOpen(false);
      const cf = await ensureCashfree(data.cashfree_env);
      const result = await cf.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_modal" });
      if (result?.error) { toast.error(result.error?.message || "Payment cancelled"); navigate(`/bookings/${data.booking.id}`); return; }
      try {
        await api.post("/bookings/verify", { booking_id: data.booking.id, order_id: data.cashfree_order_id });
        toast.success(`₹${amt.toLocaleString("en-IN")} received by LocaNest — the broker has been notified`);
      } catch {
        toast.info("Payment received — confirming with Cashfree. Check the booking page shortly.");
      }
      navigate(`/bookings/${data.booking.id}`);
    } catch (err) {
      toast.error(err?.response ? formatApiErrorDetail(err.response.data?.detail) : (err?.message || "Could not start payment"));
    } finally { setBusy(false); }
  };

  const solid = variant === "solid";
  return (
    <>
      <Button
        onClick={() => (enabled ? setOpen(true) : toast.info("Online payments are being set up — please check back soon."))}
        variant={solid ? "default" : "outline"}
        className={`w-full rounded-md ${solid ? "bg-[#2B3A67] hover:bg-[#1f2c52] text-white" : "border-[#2B3A67]/40 text-[#2B3A67] hover:bg-[#2B3A67]/5"} ${enabled ? "" : "opacity-70"} ${className}`}
        data-testid="protected-pay-btn"
      >
        {enabled ? <HandCoins size={15} className="mr-1.5" /> : <Lock size={14} className="mr-1.5" />}
        Payment to Broker{ownerSet ? ` · ₹${ownerSet.toLocaleString("en-IN")}` : ""} (via LocaNest)
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="protected-pay-dialog">
          <DialogHeader>
            <DialogTitle>Payment to Broker · {property?.title}</DialogTitle>
            <DialogDescription>
              Pay {property?.owner_name || "the owner/broker"} through LocaNest instead of directly. The money is collected by LocaNest and transferred to their verified bank/UPI within 3–4 days.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Amount (₹)</Label>
              <div className="flex items-center gap-2 rounded-md border border-[#1A1F2C]/15 bg-[#FDFBF7] px-3">
                <IndianRupee size={14} className="text-[#5C6B7A]" />
                <Input inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder={`Minimum ₹${minInr}`} className="border-0 shadow-none focus-visible:ring-0 px-0" data-testid="protected-pay-amount" />
              </div>
              <div className="mt-1.5 text-xs text-[#5C6B7A]" data-testid="protected-pay-amount-hint">
                {ownerSet
                  ? <>Owner/broker has set <strong className="text-[#1A1F2C]">₹{ownerSet.toLocaleString("en-IN")}</strong> ({PURPOSE_LABEL[property.expected_payment_purpose] || "token"}) for this listing. Adjust only if you agreed a different amount in chat.</>
                  : <>The owner/broker has not set an amount — enter the amount you agreed with them in chat / on call.</>}
              </div>
              {property?.rent > 0 && (
                <div className="mt-1.5 flex gap-1.5 flex-wrap text-xs">
                  {[...new Set([ownerSet, Math.round(property.rent * 0.1), property.rent].filter(Boolean))].map((v) => (
                    <button key={v} type="button" onClick={() => setAmount(String(v))} className="px-2 py-0.5 rounded-full border border-[#1A1F2C]/15 hover:border-[#C85A32]" data-testid={`protected-pay-preset-${v}`}>₹{v.toLocaleString("en-IN")}</button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>What is this payment for?</Label>
              <Select value={purpose} onValueChange={setPurpose}>
                <SelectTrigger data-testid="protected-pay-purpose"><SelectValue /></SelectTrigger>
                <SelectContent>{PURPOSES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Note for the broker (optional)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value.slice(0, 300))} placeholder="e.g. Token for 2BHK, move-in 1st July" data-testid="protected-pay-note" />
            </div>
            <div className="rounded-md bg-[#2B3A67]/5 border border-[#2B3A67]/20 p-3 text-xs text-[#1A1F2C] flex gap-2" data-testid="protected-pay-disclaimer">
              <ShieldCheck size={16} className="shrink-0 text-[#2B3A67] mt-0.5" />
              <span><strong>Pay through LocaNest to stay protected.</strong> We hold your payment and transfer it to the owner within 3–4 days — commission-free. If you pay a broker/owner directly outside LocaNest, we cannot guarantee your money or help resolve any dispute.</span>
            </div>
            <div className="text-[11px] text-[#5C6B7A]">Pay with UPI (Google Pay, PhonePe, Paytm, any UPI ID), debit/credit card or net-banking via Cashfree. You&rsquo;ll get an instant receipt.</div>
          </div>
          <DialogFooter>
            <Button onClick={pay} disabled={busy || !amt} className="bg-[#2B3A67] hover:bg-[#1f2c52] text-white rounded-md w-full" data-testid="protected-pay-submit">
              {busy ? "Opening secure checkout…" : `Pay ₹${amt ? amt.toLocaleString("en-IN") : "—"} to broker via LocaNest`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
